import { faFileCode, faPaperclip, faTimeline, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import ContentCellNowrap from "../../../components/table/content-cell-nowrap";
import { IInvoiceResult } from "../../../interfaces/invoice";
import ContentCellDate from "../../../components/table/content-cell-date";
import ContentCellMoney from "../../../components/table/content-cell-money";
import React from "react";
import { ISupplierInvoiceRecordResult } from "../../../interfaces/supplier-invoice-record";
import { renderClassNameStatusInvoice } from "../../../utils/helper";
import AiLimitationCell from "./ai-limitation-cell";
import AiLimitationFeedbackCell from "./ai-limitation-feedback-cell";
import { InvoiceServices } from "../../../services/invoice";

const contentError = (record: IInvoiceResult) => {
  const { processErrors } = record;
  if (!processErrors?.length) return null;
  const content = () => (
    <ul style={{ margin: "0", padding: "5px  5px 5px 25px" }}>
      {processErrors.map((item) => (
        <li key={item.field}>{item.error}</li>
      ))}
    </ul>
  );
  return (
    <Tooltip title={content()} placement="right">
      <FontAwesomeIcon icon={faTriangleExclamation} className="text-warning" />
    </Tooltip>
  );
};

export const getColumns = (
  onDownloadAttachment: (id: number, attachmentId: number, fileName: string, fileType?: string) => {},
  linkToAudit: (id: string) => void,
  currentPage: number,
  pageSize: number
): ColumnsType<IInvoiceResult> => {
  const allColumns: ColumnsType<IInvoiceResult> = [
    {
      title: "#",
      key: "index",
      width: 60,
      fixed: "left",
      align: "center",
      render: (_: any, __: IInvoiceResult, index: number) => {
        const serialNumber = (currentPage - 1) * pageSize + index + 1;
        return serialNumber;
      },
    },
    {
      title: "",
      key: "error",
      width: 40,
      fixed: "left",
      align: "center",
      render: (_: any, record: IInvoiceResult) => <>{contentError(record)}</>,
    },
    {
      title: "Supplier Code",
      dataIndex: "supplierCode",
      key: "supplierCode",
      width: 100,
      fixed: "left",
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Supplier Name",
      dataIndex: "supplierNameSystem",
      key: "supplierNameSystem",
      width: 150,
      fixed: "left",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      render: (value: string) => <ContentCellNowrap>{value}</ContentCellNowrap>,
    },
    {
      title: "Supplier Name (AI)",
      dataIndex: "supplierName",
      key: "supplierName",
      width: 150,
      fixed: "left",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      render: (value: string) => <ContentCellNowrap>{value}</ContentCellNowrap>,
    },
    {
      title: "Received Date",
      dataIndex: "receivedAt",
      key: "receivedAt",
      render: (value) => <ContentCellDate value={value} mode="datetime" />,
      width: 150,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "receivedAt",
      render: (value) => <ContentCellDate value={value} mode="datetime" />,
      width: 150,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 120,
      sorter: true,
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (value) => <ContentCellDate value={value} />,
      width: 120,
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => <div className={renderClassNameStatusInvoice(value)}>{value}</div>,
      width: 100,
      sorter: true,
    },
    {
      title: "Booking Reference",
      dataIndex: "bookingNos",
      key: "bookingNos",
      render: (bookingNos: string[] | null, record) => {
        if (!bookingNos?.length) return <span></span>;

        if (bookingNos.length > 2) {
          return <span>Multiple</span>;
        }

        return (
          <>
            {bookingNos.map((no) => (
              <React.Fragment key={`${record.id}-${no}`}>
                {no}
                <br />
              </React.Fragment>
            ))}
          </>
        );
      },
      width: 120,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: 80,
      sorter: true,
      align: "center",
    },
    {
      title: "Total Cost",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (value) => <ContentCellMoney value={value} />,
      width: 100,
      sorter: true,
      align: "right",
    },
    {
      title: "Attachment",
      dataIndex: "attachments",
      key: "attachments",
      width: 250,
      render: (_, record: IInvoiceResult) => {
        if (!record.attachments?.length) return null;

        return (
          <>
            {record.attachments.map(att => {
              const shortName =
                att.fileName.length > 35
                  ? att.fileName.slice(0, 30) + "..."
                  : att.fileName;

              return (
                <Tooltip title={att.fileName} key={att.id}>
                  <Button
                    type="link"
                    onClick={() =>
                      onDownloadAttachment(Number(record.id), att.id, att.fileName)
                    }
                    style={{ maxWidth: 300, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}
                  >
                    {shortName}
                  </Button>
                </Tooltip>
              );
            })}
          </>
        );
      },
    },
    {
      title: "AI Limitation",
      dataIndex: "aiLimitation",
      key: "aiLimitation",
      width: 200,
      render: (_: any, record: IInvoiceResult) => (
        <AiLimitationCell
          record={record}
          onSave={async (id, value) => {
            await InvoiceServices.changeAiLimitation(String(id), value); // API save
          }}
        />
      ),
      align: "center",
    },
    {
      title: "TTS Feedback",
      dataIndex: "aiLimitationFeedback",
      key: "aiLimitationFeedback",
      width: 200,
      render: (_: any, record: IInvoiceResult) => (
        <AiLimitationFeedbackCell
          record={record}
          onSave={async (id, value) => {
            await InvoiceServices.changeAiLimitation(String(id), value); // API save
          }}
        />
      ),
      align: "center",
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      align: "left",
      render: (_: any, record: IInvoiceResult) => {
        const att = record.attachments?.[0];

        return (
          <div className="flex flex-col items-center gap-1">
            {/* View Detail */}
            <Button type="link" size="small" onClick={(e) => {
              e.stopPropagation();
              linkToAudit(record.id);
            }}>
              <FontAwesomeIcon icon={faTimeline} />
            </Button>
            <Tooltip title={record.attachmentName}>
              <Button
                type="link"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownloadAttachment(Number(record.id), att?.id!, att?.fileName!);
                }}
                disabled={!att}
              >
                <FontAwesomeIcon icon={faPaperclip} />
              </Button>
            </Tooltip>

            {/* Downloads */}
            {att && (
              <div className="flex gap-1">
                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    onDownloadAttachment(Number(record.id), att.id, att.fileName, "data")
                  }
                >
                  <FontAwesomeIcon icon={faFileCode} />
                </Button>

                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    onDownloadAttachment(Number(record.id), att.id, att.fileName, "debug")
                  }
                >
                  <FontAwesomeIcon icon={faFileCode} style={{ color: "orange" }} />
                </Button>
              </div>
            )}
          </div>
        );
      },
    }
  ];

  return allColumns;
};

export const getExpandedColumns = (
): ColumnsType<IInvoiceResult> => {
  const allColumns: ColumnsType<IInvoiceResult> = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => <div className={renderClassNameStatusInvoice(value)}>{value}</div>,
      width: 100,
      sorter: true,
    },
    {
      title: "Reference",
      render: (value) => value ?? '---',
      width: 150,
      sorter: true,
    },
    {
      title: "Lead Pax",
      render: (value) => value ?? '---',
      width: 150,
      sorter: true,
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (value) => value ?? '---',
      width: 80,
      sorter: true,
      align: "center",
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (value) => value ? <ContentCellMoney value={value} /> : '---',
      width: 250,
      sorter: true,
      align: "right",
    },
  ];

  return allColumns;
};

export const getExpandedRecordColumns = (
): ColumnsType<ISupplierInvoiceRecordResult> => {
  const allColumns: ColumnsType<ISupplierInvoiceRecordResult> = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => <div className={renderClassNameStatusInvoice(value)}>{value}</div>,
      width: 100,
      sorter: true,
    },
    {
      title: "Reference",
      dataIndex: "bookingNo",
      key: "bookingNo",
      render: (value) => value ?? '---',
      width: 150,
      sorter: true,
    },
    {
      title: "Lead Pax",
      dataIndex: "leadPax",
      key: "leadPax",
      render: (value) => value ?? '---',
      width: 150,
      sorter: true,
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (value) => value ?? '---',
      width: 80,
      sorter: true,
      align: "center",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value) => value ? <ContentCellMoney value={value} /> : '---',
      width: 250,
      sorter: true,
      align: "right",
    },
  ];

  return allColumns;
};
